import React from "react";
import { Box, Card, CardContent, Chip, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { addMonths, daysInMonth, getTodayInTZ, MONTH_NAMES, ymd } from "../../BalanceSheet/utils/format";
import { BriefMode, BriefSelection } from "../types";

const MODES: { mode: BriefMode; label: string }[] = [
  { mode: "monthly", label: "Monthly" },
  { mode: "three_months", label: "3 Months" },
  { mode: "custom", label: "Custom Range" },
];

/** [ Monthly ] [ 3 Months ] [ Custom Range ]  +  month/year (ending month for 3 Months) or start/end dates. */
const BriefControls: React.FC<{ selection: BriefSelection; onChange: (s: BriefSelection) => void }> = ({ selection, onChange }) => {
  const today = getTodayInTZ();
  const years: number[] = [];
  for (let y = today.year + 1; y >= today.year - 6; y--) years.push(y);

  const switchMode = (mode: BriefMode) => {
    if (mode === selection.mode) return;
    if (mode === "custom") {
      // Start from the months currently shown.
      const { year, month } = selection.mode === "custom" ? today : selection;
      const first = selection.mode === "three_months" ? addMonths(year, month, -2) : { year, month };
      onChange({ mode, startDate: ymd(first.year, first.month, 1), endDate: ymd(year, month, daysInMonth(year, month)) });
    } else if (selection.mode === "custom") {
      const [y, m] = selection.endDate.split("-").map(Number);
      onChange({ mode, year: y || today.year, month: m || today.month });
    } else {
      onChange({ ...selection, mode });
    }
  };

  return (
    <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
      <CardContent sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", "&:last-child": { pb: 2 } }}>
        <Box display="flex" gap={1} flexWrap="wrap">
          {MODES.map((m) => (
            <Chip
              key={m.mode}
              label={m.label}
              clickable
              size="small"
              onClick={() => switchMode(m.mode)}
              color={selection.mode === m.mode ? "primary" : "default"}
              variant={selection.mode === m.mode ? "filled" : "outlined"}
            />
          ))}
        </Box>

        {selection.mode !== "custom" ? (
          <Box display="flex" alignItems="center" gap={1} sx={{ ml: { md: "auto" } }}>
            <Tooltip title="Previous month">
              <IconButton
                size="small"
                aria-label="Previous month"
                onClick={() => onChange({ ...selection, ...addMonths(selection.year, selection.month, -1) })}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <TextField
              select
              size="small"
              label={selection.mode === "three_months" ? "Ending Month" : "Month"}
              value={selection.month}
              onChange={(e) => onChange({ ...selection, month: Number(e.target.value) })}
              sx={{ width: 150 }}
            >
              {MONTH_NAMES.map((name, i) => (
                <MenuItem key={name} value={i + 1}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Year" value={selection.year} onChange={(e) => onChange({ ...selection, year: Number(e.target.value) })} sx={{ width: 100 }}>
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="Next month">
              <IconButton
                size="small"
                aria-label="Next month"
                onClick={() => onChange({ ...selection, ...addMonths(selection.year, selection.month, 1) })}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box display="flex" gap={1.5} flexWrap="wrap" sx={{ ml: { md: "auto" } }}>
            <TextField
              size="small"
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={selection.startDate}
              onChange={(e) => onChange({ ...selection, startDate: e.target.value })}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={selection.endDate}
              onChange={(e) => onChange({ ...selection, endDate: e.target.value })}
              sx={{ width: 160 }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BriefControls;
